import {
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Link,
  Select,
  SelectOption,
  Text,
  useToast,
} from '@driftt/dds'
import { faTrash } from '@driftt/dds.icons/light/faTrash'
import { faPlus } from '@driftt/dds.icons/regular/faPlus'
import { uniq } from 'ramda'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
  AVAILABLE_OPERATORS,
  CREATE_ATTRIBUTE_OPTIONS,
  OPERATORS_LIST,
} from 'components/AIConfiguration/LeadQualification/constants'
import { useAttributeValueFactory } from 'components/AIConfiguration/LeadQualification/PersonaCard/hooks'
import { PersonaForm } from 'components/AIConfiguration/LeadQualification/PersonaCard/types'
import { isEmptyOrNil } from 'utils/index'

import { AttributeOption } from '../../types'

interface Props {
  idx: number
  attributeNameOptions:
    | {
        label: string
        value: string
      }[]
    | {
        label: string
        options: {
          label: string
          value: string
        }[]
      }[]
  isRemoveAvailable: boolean
  tableSizing: string[]
  handleRemoveAttribute: (idx: number) => void
  handleToggleRequiredAttribute: () => void
}

const AttributeRow = ({
  idx,
  isRemoveAvailable,
  attributeNameOptions,
  handleRemoveAttribute,
  handleToggleRequiredAttribute,
}: Props) => {
  const toast = useToast()
  const {
    watch,
    control,
    setValue,
    formState: { errors: { attributes: attributeErrors = {} } = {} },
  } = useFormContext<PersonaForm>()

  const operator = watch(`attributes.${idx}.operator`)
  const name = watch(`attributes.${idx}.name`) || {}

  const { type = '', options } = name as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const formattedOptions =
    options &&
    options.length >= 0 &&
    uniq(options).map((option) => ({ value: option, label: option }))

  const { component, validation, defaultValue } = useAttributeValueFactory(
    operator?.value
  )

  const FILTERED_OPERATORS_LIST =
    type === 'ENUMARRAY'
      ? OPERATORS_LIST.filter(
          (item) => item.value === 'in' || item.value === 'equals'
        )
      : OPERATORS_LIST

  const hideInput = operator?.value === AVAILABLE_OPERATORS.EXISTS

  const handleAttributeChange = (
    attr: AttributeOption,
    onChange: (option: { label: string; value: string }) => void
  ) => {
    if (!isEmptyOrNil(attr?.href)) {
      window.location.pathname = attr?.href
      return
    }
    onChange(attr as { label: string; value: string })
  }

  const removeAttribute = (index: number) => {
    if (!isRemoveAvailable) {
      toast({
        message:
          'Qualification cannot be deleted as at least one rule is required.',
        variant: 'error',
      })
      return
    }
    handleRemoveAttribute(index)
  }

  return (
    <Flex
      css={{ padding: 'calc($1 - $half) 0 $1' }} // force the padding to be equal 8px
      fullWidth
    >
      {/* Attribute */}
      <Flex gap={0} css={{ width: '100%', maxWidth: '300px' }}>
        <Controller
          name={`attributes.${idx}.name`}
          control={control}
          rules={{ required: 'Attribute required' }}
          render={({ field: { value, onChange } }) => (
            <Select
              label=""
              noOptionsMessage={() => (
                <Flex direction="column" fullWidth>
                  {CREATE_ATTRIBUTE_OPTIONS.map(({ href, label }) => (
                    <Link href={href}>
                      <Flex
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-start"
                        fullWidth
                      >
                        <Icon icon={faPlus} css={{ color: '$black' }} />
                        <Text>{label}</Text>
                      </Flex>
                    </Link>
                  ))}
                </Flex>
              )}
              options={
                [
                  ...CREATE_ATTRIBUTE_OPTIONS,
                  ...attributeNameOptions,
                ] as SelectOption[]
              }
              onChange={(option) =>
                handleAttributeChange(option as AttributeOption, onChange)
              }
              value={
                typeof value === 'string'
                  ? {
                      label: value,
                      value,
                    }
                  : value
              }
              error={attributeErrors[idx]?.name?.message}
            />
          )}
        />
      </Flex>

      {/* Condition */}
      <Flex gap={0} css={{ width: '100%', maxWidth: '300px' }}>
        <Controller
          name={`attributes.${idx}.operator`}
          control={control}
          rules={{ required: 'Operator required' }}
          render={({ field: { value, onChange } }) => (
            <div title={value?.label} style={{ width: '100%' }}>
              <Select
                label=""
                options={FILTERED_OPERATORS_LIST}
                onChange={(value) => {
                  setValue(`attributes.${idx}.value`, '', {
                    shouldValidate: true,
                  })
                  onChange(value)
                }}
                value={value}
                error={attributeErrors[idx]?.operator?.message}
              />
            </div>
          )}
        />
      </Flex>

      {/* Value */}
      {hideInput ? (
        <Flex gap={0} css={{ width: '100%', maxWidth: '300px' }}>
          <Text variant="body" css={{ width: '280px' }}></Text>
        </Flex>
      ) : (
        <Flex
          gap={0}
          css={{
            width: '100%',
            maxWidth: '300px',
            marginTop: '$half',
            'label, span': { display: 'none' },
          }}
        >
          <Controller
            name={`attributes.${idx}.value`}
            control={control}
            rules={validation}
            render={({ field: { value, onChange } }) => {
              return component({
                onChange,
                error: attributeErrors[idx]?.value?.message,
                value: Boolean(value?.length) ? value : defaultValue,
                options: formattedOptions,
              })
            }}
          />
        </Flex>
      )}

      {/* Required Checkbox */}
      <Controller
        name={`attributes.${idx}.required`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Flex
            gap={0}
            css={{
              width: '100%',
              maxWidth: '33px',
              marginLeft: 'auto',
              padding: '$1 0 0 $2',
              alignItems: 'center',
            }}
          >
            <Checkbox
              label=""
              checked={value}
              onChange={() => {
                const newValue = !value
                onChange(newValue)
                handleToggleRequiredAttribute()
              }}
            />
          </Flex>
        )}
      />

      {/* Trash can */}
      <Flex
        gap={0}
        css={{
          width: '100%',
          maxWidth: '38px',
          pointerEvents: 'auto',
        }}
      >
        <IconButton
          title="remove attribute"
          icon={faTrash}
          css={{ marginTop: '$half' }}
          onClick={() => removeAttribute(idx)}
        />
      </Flex>
    </Flex>
  )
}

export default AttributeRow
